import {
  EmrPlus,
  EmrPrinter,
  EmrTag as TagIcon,
  EmrTrashcan,
  SiChevronLeft,
  SiChevronRight,
  SiZoomIn,
  SiZoomOut,
} from "@medstonetech/slate-icons";

import {
  Box,
  BoxProps,
  Button,
  chakra,
  HStack,
  Icon,
  IconButton,
  InputGroup,
  InputRightElement,
  Portal,
  Spinner,
  StackProps,
  Text,
  useControllableProp,
  useControllableState,
} from "@chakra-ui/react";
import { useFileUrl } from "hooks";
import * as React from "react";
import { Document, Page, pdfjs } from "react-pdf";
import type { PageProps, PDFPageProxy } from "react-pdf";
import { Input, Loading } from "shared";

import {
  PdfViewerContextProvider,
  PdfViewerContextValue,
  usePdfViewerContext,
} from "./PdfViewerContext";
import { debounce } from "throttle-debounce";

// Vite-compatible worker for react-pdf@5 / pdfjs-dist@2.x (no entry.webpack).
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/legacy/build/pdf.worker.min.js",
  import.meta.url
).toString();

type PdfViewerActionsProps = {
  isSubmitLoading?: boolean;
  isDeleteLoading?: boolean;
  showPrint?: boolean;
  showSubmit?: boolean;
  showDelete?: boolean;
} & StackProps;

const ChakraDocument = chakra(Document);
const ChakraPage = chakra(Page);

function PdfViewerPageController() {
  const { numPages, activePageNumber, turnPage } = usePdfViewerContext();
  const hasPreviousPage = activePageNumber > 1 && numPages > 0;
  const hasNextPage = activePageNumber < numPages;

  return (
    <Box
      width="100%"
      display="flex"
      alignItems="center"
      bg="gray.50"
      height="50px"
      justifyContent="center"
      maxH="100px"
    >
      <HStack spacing="120px">
        <IconButton
          aria-label="previous page"
          variant="icon"
          icon={
            <Icon
              as={SiChevronLeft}
              w="0.75rem"
              h="1.25rem"
              color={hasPreviousPage ? "blue" : "gray.550"}
            />
          }
          isDisabled={!hasPreviousPage}
          onClick={() => turnPage(activePageNumber - 1)}
        />
        <Box fontWeight="500" color="gray.650">
          {`${numPages === 0 ? 0 : activePageNumber} of ${numPages}`}
        </Box>
        <IconButton
          aria-label="previous page"
          variant="icon"
          icon={
            <Icon
              as={SiChevronRight}
              w="0.75rem"
              h="1.25rem"
              color={hasNextPage ? "blue" : "gray.550"}
            />
          }
          isDisabled={!hasNextPage}
          onClick={() => turnPage(activePageNumber + 1)}
        />
      </HStack>
    </Box>
  );
}

function PdfViewerSmallPageController() {
  const { numPages, activePageNumber, turnPage } = usePdfViewerContext();
  const hasPreviousPage = activePageNumber > 1 && numPages > 0;
  const hasNextPage = activePageNumber < numPages;
  const [error, setError] = React.useState(false);
  const [page, setPage] = React.useState(activePageNumber);

  const debounceChange = React.useMemo(
    () =>
      debounce(1000, (currentPage: number) => {
        if (isNaN(currentPage) || currentPage > numPages || currentPage < 1) {
          setError(true);
          return;
        }
        turnPage(currentPage);
        setError(false);
      }),
    [turnPage, setError, numPages]
  );

  React.useEffect(() => {
    setPage(activePageNumber);
  }, [activePageNumber]);

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      maxH="100px"
    >
      <HStack spacing="20px">
        <Box
          fontWeight="500"
          color="gray.650"
          display="flex"
          alignItems="center"
          gap={2}
        >
          <Input
            width="48px"
            value={page}
            type="number"
            onChange={(e) => {
              const value = Number(e.target.value);
              setPage(value);
              debounceChange(value);
            }}
            isInvalid={error}
            textAlign="end"
          />
          <Text>{`/ ${numPages}`}</Text>
        </Box>
        <IconButton
          aria-label="previous page"
          variant="icon"
          icon={
            <Icon
              as={SiChevronLeft}
              w="0.75rem"
              h="1.25rem"
              color={hasPreviousPage ? "blue" : "gray.550"}
            />
          }
          isDisabled={!hasPreviousPage}
          onClick={() => turnPage(activePageNumber - 1)}
        />

        <IconButton
          aria-label="previous page"
          variant="icon"
          icon={
            <Icon
              as={SiChevronRight}
              w="0.75rem"
              h="1.25rem"
              color={hasNextPage ? "blue" : "gray.550"}
            />
          }
          isDisabled={!hasNextPage}
          onClick={() => turnPage(activePageNumber + 1)}
        />
      </HStack>
    </Box>
  );
}

function PdfViewerPageIndicator() {
  const { numPages, activePageNumber } = usePdfViewerContext();
  return (
    <chakra.span color="blue">
      {`${numPages === 0 ? 0 : activePageNumber} of ${numPages}`}
    </chakra.span>
  );
}

type PdfViewerHeaderProps = {
  leftElements?: React.ReactNode[];
  rightElements?: React.ReactNode[];
  middleRightElements?: React.ReactNode[];
  middleLeftElements?: React.ReactNode[];
} & Omit<BoxProps, "children">;

function PdfViewerHeader(props: PdfViewerHeaderProps) {
  const {
    leftElements,
    rightElements,
    middleLeftElements,
    middleRightElements,
    title,
    ...restProps
  } = props;

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      height="80px"
      borderBottom="1px"
      borderColor="gray.450"
      padding="30px 30px 30px 44px"
      position="relative"
      {...restProps}
    >
      <HStack>{leftElements}</HStack>
      <HStack>{middleLeftElements}</HStack>
      <Box fontSize="0.9375rem">{title}</Box>
      <HStack>{middleRightElements}</HStack>
      <HStack>{rightElements}</HStack>
    </Box>
  );
}

function PdfViewerActions(props: PdfViewerActionsProps) {
  const { value, onDelete, fileUrl, onChange, onPrint } = usePdfViewerContext();
  const {
    isSubmitLoading,
    isDeleteLoading,
    showDelete = true,
    showPrint = true,
    showSubmit = true,
    children,
  } = props;
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleSubmitClick = () => {
    inputRef.current?.click();
  };

  return (
    <HStack spacing="2.5rem" maxWidth="270px" overflow="auto" height="40px">
      {children}
      {showSubmit && (
        <input
          key={fileUrl || "file-input"}
          ref={inputRef}
          type="file"
          accept="application/pdf"
          style={{ display: "none" }}
          onChange={onChange}
        />
      )}
      {isSubmitLoading ? (
        <Spinner color="blue" />
      ) : (
        showSubmit && (
          <IconButton
            aria-label="submit document"
            icon={<Icon as={EmrPlus} />}
            color="blue"
            variant="icon"
            onClick={handleSubmitClick}
          />
        )
      )}
      {showPrint && (
        <IconButton
          aria-label="print document"
          icon={<Icon as={EmrPrinter} color="blue" />}
          variant="icon"
          onClick={onPrint}
          isDisabled={!value}
        />
      )}
      {isDeleteLoading ? (
        <Spinner color="blue" />
      ) : (
        showDelete && (
          <IconButton
            aria-label="delete document"
            icon={<Icon as={EmrTrashcan} color="blue" />}
            variant="icon"
            onClick={onDelete}
            isDisabled={!value}
          />
        )
      )}
    </HStack>
  );
}

type CustomPageProps = Omit<PageProps, "pageNumber"> & {
  pageNumber: number;
  getPageTag?: (page: number) => string | null;
  onOpenTagSelector?: (page: number) => void;
};

function CustomPage(props: CustomPageProps) {
  const { pageNumber, getPageTag, onOpenTagSelector, ...pageProps } = props;
  const { activePageNumber, turnPage } = usePdfViewerContext();
  const pageTag = getPageTag?.(pageNumber) ?? null;

  return (
    <Box
      display="flex"
      alignItems="flex-end"
      sx={{ "&:not(:first-of-type)": { marginTop: "14px" } }}
    >
      <chakra.span color="gray.550" marginRight="0.375rem" fontSize="0.75rem">
        {pageNumber}
      </chakra.span>
      <Box position="relative">
        <Button
          onClick={() => turnPage(pageNumber)}
          borderRadius="10px"
          overflow="hidden"
          height="110px"
          width="84px"
          position="relative"
          boxShadow={
            activePageNumber === pageNumber ? "0 0 0 2px #007AFF" : undefined
          }
          border="none"
          minW="none"
          padding={0}
          bg="transparent"
          _hover={{ bg: "transparent" }}
        >
          <ChakraPage
            width="100%"
            height="100%"
            pageNumber={pageNumber}
            sx={{
              "> canvas": {
                width: "100% !important",
                height: "100% !important",
              },
            }}
            renderAnnotationLayer={false}
            {...pageProps}
          />
        </Button>
        {pageTag && (
          <IconButton
            background={"transparent"}
            aria-label={`page ${pageNumber} has tag`}
            icon={<Icon as={TagIcon} color={"blue"} />}
            size="xs"
            variant="solid"
            position="absolute"
            bottom="-5px"
            right="2px"
            onClick={() => onOpenTagSelector?.(pageNumber)}
          />
        )}
      </Box>
    </Box>
  );
}

function PdfViewerPreview(props: {
  getPageTag?: (page: number) => string | null;
  onOpenTagSelector?: (page: number) => void;
}) {
  const { numPages, value } = usePdfViewerContext();
  const { getPageTag, onOpenTagSelector } = props;

  return (
    <Box
      minW="124px"
      padding="7px 14px"
      borderRight="1px"
      borderColor="gray.450"
      maxHeight="100%"
      overflow="auto"
    >
      {value && (
        <ChakraDocument
          file={value}
          loading={<Loading />}
          height="100%"
          options={{ disableAnnotationLayer: true }}
        >
          {Array(numPages)
            .fill(0)
            .map((_, index) => {
              const pageNumber = index + 1;
              return (
                <CustomPage
                  key={pageNumber}
                  pageNumber={pageNumber}
                  renderAnnotationLayer={false}
                  renderInteractiveForms={false}
                  renderTextLayer={false}
                  getPageTag={getPageTag}
                  onOpenTagSelector={onOpenTagSelector}
                />
              );
            })}
        </ChakraDocument>
      )}
    </Box>
  );
}

function PdfZoomControls() {
  const { setScale, scale } = usePdfViewerContext();
  const [zoom, setZoom] = React.useState(scale * 100);
  const [error, setError] = React.useState(false);

  const debounceChange = React.useMemo(
    () =>
      debounce(1000, (currentZoom: number) => {
        if (isNaN(currentZoom)) {
          setError(true);
          return;
        }
        setScale(currentZoom / 100);
        setError(false);
      }),
    [setScale, setError]
  );

  React.useEffect(() => {
    setZoom(scale * 100);
  }, [scale]);

  return (
    <HStack spacing="8px">
      <IconButton
        aria-label="zoom-out"
        icon={
          <Icon as={SiZoomOut} color="blue" sx={{ h: "32px", w: "32px" }} />
        }
        variant="icon"
        onClick={() => setScale(scale - 0.25)}
      />
      <Box textAlign="center">
        <InputGroup>
          <Input
            width="74px"
            value={zoom.toFixed(0)}
            type="number"
            onChange={(e) => {
              const value = Number(e.target.value);
              setZoom(value);
              debounceChange(value);
            }}
            isInvalid={error}
          />
          <InputRightElement>%</InputRightElement>
        </InputGroup>
      </Box>
      <IconButton
        aria-label="zoom-in"
        icon={<Icon as={SiZoomIn} color="blue" sx={{ h: "32px", w: "32px" }} />}
        variant="icon"
        onClick={() => setScale(scale + 0.25)}
      />
      {(scale * 100).toFixed(0)}%
    </HStack>
  );
}

type PdfViewerActivePageProps = BoxProps & {
  pageWidth?: number;
  pageHeight?: number;
  /**
   * Scales the page down so it fits entirely within the available space,
   * instead of rendering it at a fixed `pageWidth`. Overrides `pageWidth`
   * and `pageHeight`.
   */
  fitToContainer?: boolean;
  renderTagSelector?: (page: number) => React.ReactNode;
};

function PdfViewerActivePage(props: PdfViewerActivePageProps) {
  const { pageWidth, pageHeight, fitToContainer, renderTagSelector, ...rest } =
    props;
  const { activePageNumber, setNumPages, value, scale } = usePdfViewerContext();

  // callback ref: the measured box only mounts once there is a file to render
  const [container, setContainer] =
    React.useState<Nullable<HTMLDivElement>>(null);
  const [containerSize, setContainerSize] = React.useState({
    width: 0,
    height: 0,
  });
  // width / height of the rendered page at its original size
  const [pageAspectRatio, setPageAspectRatio] = React.useState(0);

  React.useLayoutEffect(() => {
    if (!fitToContainer || !container) {
      return undefined;
    }

    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setContainerSize({ width, height });
    });
    observer.observe(container);

    return () => observer.disconnect();
  }, [fitToContainer, container]);

  const fittedPageWidth = React.useMemo(() => {
    const { width, height } = containerSize;

    if (!fitToContainer || !width || !height) {
      return undefined;
    }

    // Until the page reports its size, fill the available width
    if (!pageAspectRatio) {
      return Math.floor(width);
    }

    return Math.floor(Math.min(width, height * pageAspectRatio));
  }, [containerSize, fitToContainer, pageAspectRatio]);

  const onPageLoadSuccess = React.useCallback((page: PDFPageProxy) => {
    if (!page.originalHeight) {
      return;
    }

    setPageAspectRatio(page.originalWidth / page.originalHeight);
  }, []);

  return (
    <>
      {value ? (
        <Box
          ref={fitToContainer ? setContainer : undefined}
          position="relative"
          {...(fitToContainer && {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "10px",
            minHeight: 0,
            overflow: "hidden",
            ...rest,
          })}
        >
          {renderTagSelector && (
            <Box position="absolute" top="6px" left="6px" zIndex={1}>
              {renderTagSelector(activePageNumber)}
            </Box>
          )}
          <ChakraDocument
            file={value}
            onLoadSuccess={({ numPages: np }: { numPages: number }) =>
              setNumPages(np)
            }
            loading={<Loading />}
            overflow="auto"
            sx={{ canvas: { margin: "auto" } }}
            {...(fitToContainer ? {} : rest)}
          >
            <Page
              pageNumber={activePageNumber}
              renderAnnotationLayer={false}
              renderTextLayer={false}
              scale={scale}
              width={fitToContainer ? fittedPageWidth : pageWidth}
              height={fitToContainer ? undefined : pageHeight}
              onLoadSuccess={fitToContainer ? onPageLoadSuccess : undefined}
            />
          </ChakraDocument>
        </Box>
      ) : (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          padding="1rem"
          {...rest}
        >
          No PDF Selected...
        </Box>
      )}
    </>
  );
}

type PdfViewerContainerProps = {
  header?: React.ReactNode;
  preview?: React.ReactNode;
  activePage?: React.ReactNode;
  pageController?: React.ReactNode;
  customContent?: React.ReactNode;
  contentContainerProps?: BoxProps;
  isLoading?: boolean;
  isDisabled?: boolean;
} & BoxProps;

function PdfViewerContainer(props: PdfViewerContainerProps) {
  const {
    header,
    preview,
    activePage,
    pageController,
    customContent,
    contentContainerProps,
    isLoading = false,
    isDisabled,
    ...boxProps
  } = props;

  const { onChange, fileUrl } = usePdfViewerContext();

  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleSubmitClick = () => {
    inputRef.current?.click();
  };

  if (customContent && !fileUrl)
    return (
      <>
        {React.isValidElement(customContent) &&
          React.cloneElement(
            customContent as React.ReactElement<Record<string, unknown>>,
            {
              onClick: handleSubmitClick,
              ...(isDisabled && {
                style: { opacity: 0.5, cursor: "not-allowed" },
              }),
            }
          )}
        <Portal>
          <input
            type="file"
            ref={inputRef}
            style={{ display: "none" }}
            onChange={onChange}
            disabled={isDisabled}
          />
        </Portal>
      </>
    );

  return (
    <Box
      bg="gray.200"
      width="100%"
      display="flex"
      flexDirection="column"
      borderRadius="10px"
      border="1px"
      borderColor="gray.450"
      overflow="hidden"
      {...boxProps}
    >
      {header}
      <Box display="flex" height="calc(100% - 10px)" {...contentContainerProps}>
        {isLoading ? <></> : preview}
        <Box display="flex" flexDirection="column" flex={1} overflow="auto">
          {isLoading ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              h="72vh"
            >
              <Loading />
            </Box>
          ) : (
            <>
              {React.isValidElement(activePage) &&
                React.cloneElement(
                  activePage as React.ReactElement<{ flex?: number }>,
                  { flex: 1 }
                )}
            </>
          )}
          {pageController}
        </Box>
      </Box>
    </Box>
  );
}

type PdfViewerPublicState = Pick<
  PdfViewerContextValue,
  "activePageNumber" | "numPages" | "value"
>;

type PdfViewerProps = React.PropsWithChildren<{
  value?: Nullable<string | File | Blob>;
  numPages?: number;
  activePageNumber?: number;
  defaultValue?: string | File | Blob;
  onChange?: (value: Nullable<string | File | Blob>) => void;
  setNumPages?: (numPages: number) => void;
  setActivePageNumber?: (newActivePageNumber: number) => void;
  onDelete?: (state: PdfViewerPublicState) => void;
}>;

function PdfViewer(props: PdfViewerProps) {
  const {
    value: valueProp,
    defaultValue,
    onChange,
    onDelete,
    numPages: numPagesProp,
    setNumPages: setNumPagesProp,
    activePageNumber: activePageNumberProp,
    setActivePageNumber: setActivePageNumberProp,
    children,
  } = props;
  const [value, setValue] = useControllableState({
    defaultValue,
    onChange,
    value: valueProp,
  });
  const [isValueControlled] = useControllableProp(valueProp, value);
  const [numPages, setNumPages] = useControllableState({
    value: numPagesProp,
    onChange: setNumPagesProp,
    defaultValue: 0,
  });
  const [activePageNumber, setActivePageNumber] = useControllableState({
    value: activePageNumberProp,
    onChange: setActivePageNumberProp,
    defaultValue: 1,
  });
  const fileUrl = useFileUrl(value);
  const [scale, setScale] = React.useState(1.0);

  const contextValue: PdfViewerContextValue = React.useMemo(
    () => ({
      numPages,
      activePageNumber,
      turnPage: (page) => setActivePageNumber(page),
      setNumPages: (n) => setNumPages(n),
      onDelete: onDelete
        ? () => onDelete({ activePageNumber, numPages, value })
        : () => {
            setValue(null);
            setNumPages(0);
            setActivePageNumber(1);
          },
      onChange: isValueControlled
        ? (e: React.ChangeEvent<HTMLInputElement>) => {
            const newFile = e.target.files?.item(0);

            if (newFile) {
              setValue(newFile);
            }
          }
        : (e: React.ChangeEvent<HTMLInputElement>) => {
            const newFile = e.target.files?.item(0);

            if (newFile) {
              setNumPages(0);
              setActivePageNumber(1);
              setValue(newFile);
            }
          },
      onPrint: () => {
        if (fileUrl) {
          const myWindow = window.open(fileUrl);
          myWindow?.focus();
          myWindow?.print();
        }
      },
      value,
      fileUrl,
      scale,
      setScale,
    }),
    [
      numPages,
      activePageNumber,
      value,
      setValue,
      fileUrl,
      onDelete,
      setActivePageNumber,
      setNumPages,
      isValueControlled,
      scale,
      setScale,
    ]
  );

  return (
    <PdfViewerContextProvider value={contextValue}>
      {children}
    </PdfViewerContextProvider>
  );
}

export type { PdfViewerProps };

export {
  PdfViewer,
  PdfViewerActivePage,
  PdfViewerActions,
  PdfViewerHeader,
  PdfViewerPreview,
  PdfViewerPageIndicator,
  PdfViewerContainer,
  PdfViewerPageController,
  PdfZoomControls,
  PdfViewerSmallPageController,
};
